/**
 * The refresh-jobs table on a refresh run's show page.
 *
 * Replaces the generic pipeline-items table for this run class rather than sitting beside it.
 * The generic table reads `GET /runs/{id}/events` and would render four empty-ish columns for a
 * refresh row — `context` has nothing to say, and mode/outcome/proposals/page-gone live in
 * `metadata` jsonb — while this one reads `GET /refresh-jobs`, i.e. `v_refresh_jobs`, where those
 * are columns. Rows are still `events` rows, so double-clicking one opens the ordinary item show
 * page.
 */
import React from "react";

import { useNavigation } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { RefreshCw, X } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Card } from "@/components/ui/card";
import { refreshOutcomeLabel } from "@/lib/tina/refresh-jobs";
import { useRefreshJobsColumns } from "@/pages/tina/runs/hooks/use-refresh-jobs-columns";
import {
  type RefreshJobsFilterState,
  buildRefreshJobsFilters,
} from "@/pages/tina/runs/lib/refresh-jobs-filters";
import type { RefreshJobRow } from "@/types/tina";

interface RefreshJobsTableProps {
  filterState: RefreshJobsFilterState;
  /** Clears the outcome the tile row selected; the column filters clear themselves. */
  onClearOutcome: () => void;
  /** Bumped by the caller when the run's detail poll sees new work, to refetch. */
  refetchToken: number;
}

export function RefreshJobsTable({
  filterState,
  onClearOutcome,
  refetchToken,
}: RefreshJobsTableProps) {
  const columns = useRefreshJobsColumns();
  const { show } = useNavigation();

  const table = useTable<RefreshJobRow>({
    columns,
    refineCoreProps: {
      // `tina-runs` + an explicit `urlPath`, the same shape the show page's
      // items table uses: the resource only picks the data provider here, and
      // `/refresh-jobs` is not a routed IMS resource of its own.
      resource: "tina-runs",
      meta: { urlPath: "refresh-jobs" },
      pagination: { pageSize: 50 },
      filters: { permanent: buildRefreshJobsFilters(filterState) },
    },
    meta: {
      onDoubleClick: (
        _e: React.MouseEvent,
        row: { original: RefreshJobRow },
      ) => {
        show("tina-items", row.original.id);
      },
    },
  });

  const refetch = table.refineCore.tableQuery.refetch;
  const setCurrentPage = table.refineCore.setCurrentPage;
  const totalRows = table.refineCore.tableQuery.data?.total;

  // Reset to page 1 whenever the tile row changes the outcome, so a click never
  // strands the table on an out-of-range page of the smaller filtered set.
  const previousOutcome = React.useRef(filterState.outcome);
  React.useEffect(() => {
    if (previousOutcome.current === filterState.outcome) return;
    previousOutcome.current = filterState.outcome;
    setCurrentPage(1);
  }, [filterState.outcome, setCurrentPage]);

  React.useEffect(() => {
    if (refetchToken === 0) return;
    void refetch();
  }, [refetchToken, refetch]);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Refresh jobs</h2>
          {typeof totalRows === "number" && (
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
              {totalRows}
            </span>
          )}
          {filterState.outcome && (
            <button
              type="button"
              onClick={onClearOutcome}
              className="inline-flex items-center gap-1 rounded-full border bg-accent/60 px-2 py-0.5 text-[11px] font-medium hover:bg-accent"
            >
              {refreshOutcomeLabel(filterState.outcome)}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground">
          double-click a row to open the job
        </span>
      </div>
      <div className="p-3">
        <DataTable table={table} />
      </div>
    </Card>
  );
}
